/**
 * OAuth2 Success Page
 *
 * This page is displayed after successful OAuth2 authorization.
 * It runs in a full page redirect (not popup).
 *
 * Flow:
 * 1. User authorizes in OAuth2 provider (Google, Azure, etc.)
 * 2. Provider redirects to Cloud Function oauth2Callback
 * 3. Cloud Function saves tokens and redirects here with account_id & email
 * 4. This page redirects back to the original page (stored in sessionStorage)
 */

import { Container, Stack, Title, Text, Loader, Alert, Button } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MIL, TRES } from "@/utils/constants";

/**
 * Redirects back to the original page.
 */
function handleRedirectBack() {
  const flowStateStored = sessionStorage.getItem("oauth2_flow_state");
  let returnUrl = "/email-accounts"; // Default fallback

  if (flowStateStored) {
    try {
      const flowState = JSON.parse(flowStateStored) as { returnUrl?: string };
      if (flowState.returnUrl) {
        returnUrl = flowState.returnUrl;
      }
    } catch (error) {
      console.error("Failed to parse flow state:", error);
    }
  }

  window.location.href = returnUrl;
}

/**
 *
 */
export default function OAuth2SuccessPage() {
  const [searchParameters] = useSearchParams();
  const [autoCloseSeconds, setAutoCloseSeconds] = useState(TRES);
  const [error, setError] = useState<string | null>(null);

  const accountId = searchParameters.get("account_id");
  const email = searchParameters.get("email");

  useEffect(() => {
    // Validate parameters
    if (!accountId || !email) {
      console.error("❌ Invalid OAuth2 success parameters", { accountId, email });
      setError("Parâmetros inválidos na URL. Recarregue a página e tente novamente.");
      return;
    }

    // Get return URL from sessionStorage
    const flowStateStored = sessionStorage.getItem("oauth2_flow_state");
    let returnUrl = "/email-accounts"; // Default fallback

    if (flowStateStored) {
      try {
        const flowState = JSON.parse(flowStateStored) as { returnUrl?: string };
        if (flowState.returnUrl) {
          returnUrl = flowState.returnUrl;
        }
      } catch (error) {
        console.error("Failed to parse flow state:", error);
      }
    }

    // Auto-redirect countdown
    const interval = setInterval(() => {
      setAutoCloseSeconds((previous) => {
        if (previous <= 1) {
          // Redirect back to original page
          window.location.href = returnUrl;
          return 0;
        }
        return previous - 1;
      });
    }, MIL);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [accountId, email]);

  if (error) {
    return (
      <Container size="sm" py="xl">
        <Stack gap="lg" align="center">
          <IconX size={64} color="red" />
          <Title order={2}>Erro</Title>
          <Alert color="red" title="Erro na autenticação">
            {error}
          </Alert>
          <Button onClick={handleRedirectBack}>Voltar</Button>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="sm" py="xl">
      <Stack gap="lg" align="center">
        <IconCheck size={64} color="green" />

        <Title order={2}>Autenticação bem-sucedida!</Title>

        <Alert color="green" title="Conta conectada com sucesso">
          <Stack gap="xs">
            <Text size="sm">
              <strong>Email:</strong> {email}
            </Text>
            <Text size="sm">
              <strong>ID da conta:</strong> {accountId}
            </Text>
          </Stack>
        </Alert>

        <Stack gap="xs" align="center">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Redirecionando automaticamente em {autoCloseSeconds} segundo
            {autoCloseSeconds === 1 ? "" : "s"}...
          </Text>
          <Button size="sm" variant="subtle" onClick={handleRedirectBack}>
            Voltar agora
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
}
